import { CalendarEvent } from '../models/calendar.types';

/**
 * Calcule le dimanche de Pâques pour une année grégorienne donnée (algorithme de Meeus/Jones/Butcher).
 */
export function getEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = mars, 4 = avril
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Génère la liste des 11 jours fériés légaux français pour une année donnée sous forme de CalendarEvent.
 */
export function getFrenchHolidays(year: number): CalendarEvent[] {
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

  const makeHoliday = (slug: string, title: string, y: number, m: number, d: number): CalendarEvent => {
    const startAt = `${y}-${pad(m)}-${pad(d)}T00:00:00.000Z`;
    const endAt = `${y}-${pad(m)}-${pad(d)}T23:59:59.999Z`;
    return {
      id: `french-holiday-${y}-${slug}`,
      sourceId: 'french-holidays',
      title,
      description: 'Jour férié légal en France',
      startAt,
      endAt,
      allDay: true,
      category: 'violet',
    };
  };

  const easterSunday = getEasterSunday(year);

  // Lundi de Pâques (Pâques + 1 jour)
  const easterMonday = new Date(easterSunday);
  easterMonday.setUTCDate(easterSunday.getUTCDate() + 1);

  // Jeudi de l'Ascension (Pâques + 39 jours)
  const ascension = new Date(easterSunday);
  ascension.setUTCDate(easterSunday.getUTCDate() + 39);

  // Lundi de Pentecôte (Pâques + 50 jours)
  const pentecostMonday = new Date(easterSunday);
  pentecostMonday.setUTCDate(easterSunday.getUTCDate() + 50);

  return [
    makeHoliday('jour-de-lan', "Jour de l'An", year, 1, 1),
    makeHoliday(
      'lundi-de-paques',
      'Lundi de Pâques',
      year,
      easterMonday.getUTCMonth() + 1,
      easterMonday.getUTCDate(),
    ),
    makeHoliday('fete-du-travail', 'Fête du Travail', year, 5, 1),
    makeHoliday('victoire-1945', 'Victoire 1945', year, 5, 8),
    makeHoliday(
      'ascension',
      "Jeudi de l'Ascension",
      year,
      ascension.getUTCMonth() + 1,
      ascension.getUTCDate(),
    ),
    makeHoliday(
      'lundi-de-pentecote',
      'Lundi de Pentecôte',
      year,
      pentecostMonday.getUTCMonth() + 1,
      pentecostMonday.getUTCDate(),
    ),
    makeHoliday('fete-nationale', 'Fête Nationale', year, 7, 14),
    makeHoliday('assomption', 'Assomption', year, 8, 15),
    makeHoliday('toussaint', 'Toussaint', year, 11, 1),
    makeHoliday('armistice-1918', 'Armistice 1918', year, 11, 11),
    makeHoliday('noel', 'Noël', year, 12, 25),
  ];
}

/**
 * Récupère les jours fériés français pour une plage d'années donnée (ex: de N-1 à N+2).
 */
export function getFrenchHolidaysRange(startYear: number, endYear: number): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (let y = startYear; y <= endYear; y++) {
    events.push(...getFrenchHolidays(y));
  }
  return events;
}
